import React, { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { mediaUrl, money } from "@/web/useWebApi";
import { SectionWrap, WButton, Stars, resolveLink, useSiteLink } from "../theme";

// Demande de prestation → ouvre le formulaire de contact pré-rempli (même page ou page Contact)
export function requestContact(nav, subject, go, e) {
  try { sessionStorage.setItem("wsite-prefill", subject || ""); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent("wsite:prefill", { detail: { subject } }));
  go(e, resolveLink("#contact", nav));
}

export function ServicesSection({ section, nav }) {
  const d = section.data || {};
  const items = d.items || [];
  const go = useSiteLink();
  return (
    <SectionWrap section={section}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-testid="ws-services-grid">
        {items.map((it, i) => (
          <article key={i} className="group p-6 lg:p-7 flex flex-col border transition-colors duration-200 hover:border-current" style={{ borderColor: "var(--w-border)", background: "var(--w-card)", borderRadius: "var(--w-radius)" }} data-testid={`ws-service-${i}`}>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-50">{String(i + 1).padStart(2, "0")}</div>
            <h3 className="mt-3 text-xl font-black tracking-tight">{it.label}</h3>
            {it.description && <p className="mt-2 text-sm opacity-75 flex-1 whitespace-pre-line">{it.description}</p>}
            <div className="mt-6 flex items-end justify-between gap-3">
              <div>
                {d.show_prices !== false && <div className="text-2xl font-black" style={{ color: "var(--w-primary)" }}>{Number(it.price) > 0 ? money(it.price) : "Sur devis"}</div>}
                {it.duration && <div className="text-xs opacity-60 mt-0.5">{it.duration}</div>}
              </div>
              <WButton size="sm" variant="outline" onClick={(e) => requestContact(nav, it.label, go, e)} data-testid={`ws-service-cta-${i}`}>{d.cta_label || "Demander"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></WButton>
            </div>
          </article>
        ))}
        {!items.length && <p className="text-sm opacity-60">Aucune prestation renseignée pour le moment.</p>}
      </div>
    </SectionWrap>
  );
}

export function GallerySection({ section }) {
  const d = section.data || {};
  const images = d.images || [];
  const [open, setOpen] = useState(null);
  const masonry = d.layout === "masonry";
  return (
    <SectionWrap section={section}>
      {masonry ? (
        <div className="columns-2 md:columns-3 gap-3 [&>img]:mb-3" data-testid="ws-gallery-grid">
          {images.map((u, i) => <img key={u + i} src={mediaUrl(u)} alt="" loading="lazy" onClick={() => setOpen(u)} className="w-full break-inside-avoid cursor-zoom-in transition-opacity hover:opacity-90" style={{ borderRadius: "var(--w-radius)" }} data-testid={`ws-gallery-img-${i}`} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="ws-gallery-grid">
          {images.map((u, i) => <img key={u + i} src={mediaUrl(u)} alt="" loading="lazy" onClick={() => setOpen(u)} className="w-full aspect-square object-cover cursor-zoom-in transition-opacity hover:opacity-90" style={{ borderRadius: "var(--w-radius)" }} data-testid={`ws-gallery-img-${i}`} />)}
        </div>
      )}
      {!images.length && <p className="text-sm opacity-60">Aucune photo pour le moment.</p>}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setOpen(null)} data-testid="ws-gallery-lightbox">
          <button type="button" className="absolute top-4 right-4 text-white" aria-label="Fermer"><X className="h-7 w-7" /></button>
          <img src={mediaUrl(open)} alt="" className="max-h-[90vh] max-w-[95vw] object-contain" />
        </div>
      )}
    </SectionWrap>
  );
}

export function TeamSection({ section }) {
  const members = section.data?.members || [];
  return (
    <SectionWrap section={section}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="ws-team-grid">
        {members.map((m, i) => (
          <div key={i} className="flex flex-col" data-testid={`ws-team-${i}`}>
            {m.photo_url ? <img src={mediaUrl(m.photo_url)} alt={m.name} className="w-full aspect-square object-cover" style={{ borderRadius: "var(--w-radius)" }} />
              : <div className="w-full aspect-square flex items-center justify-center text-5xl font-black" style={{ background: "var(--w-primary)", color: "var(--w-on-primary)", borderRadius: "var(--w-radius)" }}>{(m.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}</div>}
            <div className="mt-4 text-lg font-black tracking-tight">{m.name}</div>
            {m.role && <div className="text-xs uppercase tracking-[0.2em] font-bold mt-0.5" style={{ color: "var(--w-primary)" }}>{m.role}</div>}
            {m.bio && <p className="mt-2 text-sm opacity-75">{m.bio}</p>}
          </div>
        ))}
        {!members.length && <p className="text-sm opacity-60">Équipe à présenter.</p>}
      </div>
    </SectionWrap>
  );
}

export function ReviewsSection({ section }) {
  const d = section.data || {};
  const manual = d.source === "pag" ? [] : (d.items || []);
  const pag = d.source === "manual" ? [] : (d.pag_items || []);
  const all = [...pag.map((r) => ({ ...r, verified: true })), ...manual];
  const stats = d.pag_stats;
  return (
    <SectionWrap section={section} muted>
      {stats?.count > 0 && <div className="mb-8 flex items-center gap-3 text-sm" data-testid="ws-reviews-stats"><Stars n={Math.round(stats.average || 0)} className="h-5 w-5" /><b>{stats.average} / 5</b><span className="opacity-60">· {stats.count} avis clients vérifiés PAG</span></div>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ws-reviews-grid">
        {all.map((r, i) => (
          <blockquote key={i} className="p-6 flex flex-col" style={{ background: "var(--w-card)", border: "1px solid var(--w-border)", borderRadius: "var(--w-radius)" }} data-testid={`ws-review-${i}`}>
            <Stars n={Number(r.rating) || 5} />
            <p className="mt-4 text-sm lg:text-base leading-relaxed flex-1">« {r.text} »</p>
            <footer className="mt-5 text-xs font-bold uppercase tracking-[0.15em] flex items-center gap-2">{r.author || "Client"} {r.verified && <span className="text-[9px] px-1.5 py-0.5 normal-case tracking-normal" style={{ background: "var(--w-accent)", color: "var(--w-on-accent)" }}>Avis vérifié</span>}</footer>
            {r.reply && <div className="mt-3 text-xs opacity-70 border-t pt-3" style={{ borderColor: "var(--w-border)" }}>Réponse : {r.reply}</div>}
          </blockquote>
        ))}
        {!all.length && <p className="text-sm opacity-60">Les premiers avis apparaîtront ici.</p>}
      </div>
    </SectionWrap>
  );
}
